/**
 * Centralized API endpoint constants.
 * Base URL is read from the Axios client (API_BASE_URL env var).
 */
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/v1/Auth/Login',
    LOGIN_SSO: '/api/v1/Auth/LoginSSO',
    REFRESH_TOKEN: '/api/v1/Auth/RefreshToken',
    REVOKE_TOKEN: '/api/v1/Auth/RevokeToken',
    FORGOT_PASSWORD: '/api/v1/Auth/ForgotPassword',
    VERIFY_PASSWORD_RESET_OTP: '/api/v1/Auth/VerifyPasswordResetOtp',
    VALIDATE_PASSWORD_RESET_TOKEN: '/api/v1/Auth/ValidatePasswordResetToken',
    RESET_PASSWORD: '/api/v1/Auth/ResetPassword'
  },
  REGISTRATION: {
    CREATE_ACCOUNT: '/api/v1/Registration/OnboardingCreateAccount',
    CREATE_ACCOUNT_SSO: '/api/v1/Registration/OnboardingCreateAccountSSO',
    PROFILE_INFO: '/api/v1/Registration/OnboardingProfileInfo',
    QUESTIONNAIRE: '/api/v1/Registration/OnboardingQuestionnaire',
  },
  ACCOUNT: {
    CHANGE_PASSWORD: '/api/v1/Account/ChangePassword',
    DELETE_MY_ACCOUNT: '/api/v1/Account/DeleteMyAccount',
  },
  USER_PROFILE: {
    GET_MY_PROFILE: '/api/v1/UserProfile/GetMyProfile',
    UPSERT_PROFILE: '/api/v1/UserProfile/UpsertProfile',
    GET_OTHER_USER_PROFILE: '/api/v1/UserProfile/GetOtherUserProfile',
    GET_CHART_DATA: '/api/v1/UserProfile/GetChartData',
    UPDATE_USER_MEASUREMENT: '/api/v1/UserProfile/UpdateUserMeasurement',
    USER_RELATION: '/api/v1/UserProfile/UserRelation',
    INVITE_STATUS: '/api/v1/UserProfile/InviteStatus',
    GET_USER_CONNECTIONS: '/api/v1/UserProfile/GetUserConnections',
    BUDDY_SEARCH: '/api/v1/UserProfile/BuddySearch',
    UPSERT_PROFILE_PRIVACY_SETTINGS: '/api/v1/UserProfile/UpsertProfilePrivacySettings',
    GET_ACCOUNT_SETTINGS: '/api/v1/UserProfile/GetAccountSettings',
    UPDATE_ACCOUNT_SETTINGS: '/api/v1/UserProfile/UpdateAccountSettings',
    LEADERBOARD_LIST: '/api/v1/UserProfile/LeaderBoardList',
    AWARDS: '/api/v1/UserProfile/Awards',
    REPORT_USER: '/api/v1/UserProfile/ReportUser',
    GET_USER_BUDDY_PREFERENCES: '/api/v1/UserProfile/GetUserBuddyPreferences',
    BUDDY_MATCHING: '/api/v1/UserProfile/BuddyMatching',
  },
  NOTIFICATION: {
    NOTIFICATION: '/api/v1/Notification/Notification',
    DELETE_ALL_NOTIFICATIONS: '/api/v1/Notification/DeleteAllNotifications',
    UPDATE_READ_STATUS: '/api/v1/Notification/UpdateReadStatus',
    GET_NOTIFICATIONS: '/api/v1/Notification/GetNotifications',
    COUNT: '/api/v1/Notification/Count',
  },
  AGENT: {
    AGENTS_LIST: '/api/v1/Agent/AgentsList',
    AGENT_DETAILS: '/api/v1/Agent/AgentDetails',
    /** POST creates or edits an agent (multipart); DELETE flips its status. */
    AGENT: '/api/v1/Agent/Agent',
    PERSONA_LIST: '/api/v1/Agent/PersonaList',
    AI_BUDDY_SETTINGS: '/api/v1/Agent/AiBuddySettings',
    SUGGEST_COMMENT: '/api/v1/Agent/SuggestComment',
  },
  FEEDS: {
    GET_USER_POSTS: '/api/v1/Feeds/GetUserPosts',
    GET_FEEDS: '/api/v1/Feeds/GetFeeds',
    GET_POST: '/api/v1/Feeds/GetPost',
    POST: '/api/v1/Feeds/Post',
    COMMENT: '/api/v1/Feeds/Comment',
    GET_COMMENTS_LIST: '/api/v1/Feeds/GetCommentsList',
    COMMENT_REACTION: '/api/v1/Feeds/CommentReaction',
    POST_REACTION: '/api/v1/Feeds/PostReaction',
    SAVE_UNSAVE_POST: '/api/v1/Feeds/SaveUnsavePost',
    GET_SAVED_POSTS: '/api/v1/Feeds/GetSavedPosts',
    SHARE_POST: '/api/v1/Feeds/SharePost',
    REPORT_POST: '/api/v1/Feeds/ReportPost',
    FILTER_POSTS: '/api/v1/Feeds/FilterPosts',
    GET_POST_REACTION_LIST: '/api/v1/Feeds/GetPostReactionList',
  },
  FILE_UPLOAD: {
    UPLOAD: '/api/v1/FileUpload/upload',
  },
  CONVERSATION: {
    INITIATE: '/api/v1/Conversation/Initiate',
    LIST: '/api/v1/Conversation/List',
    LIST_WITH_FILTER: '/api/v1/Conversation/ListWithFilter',
    DELETE: '/api/v1/Conversation/Delete',
    SEARCH_USERS: '/api/v1/Conversation/SearchUsers',
    BY_ID: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}`,
    SEND_MESSAGE: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/SendMessage`,
    UPDATE_MESSAGE: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/UpdateMessage`,
    DELETE_MESSAGE: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/DeleteMessage`,
    MESSAGES: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/Messages`,
    MARK_MESSAGE_READ: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/MarkMessageRead`,
    UNREAD_COUNT: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/UnreadCount`,
    ADD_ATTACHMENT: (conversationId: number | string, messageId: number | string) =>
      `/api/v1/Conversation/${conversationId}/Message/${messageId}/AddAttachment`,
    ATTACHMENTS: (conversationId: number | string, messageId: number | string) =>
      `/api/v1/Conversation/${conversationId}/Message/${messageId}/Attachments`,
    ADD_PARTICIPANT: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/AddParticipant`,
    REMOVE_PARTICIPANT: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/RemoveParticipant`,
    PARTICIPANTS: (conversationId: number | string) =>
      `/api/v1/Conversation/${conversationId}/Participants`,
  },
} as const;
